import type { DragEndEvent } from '@dnd-kit/core';
import type {
  AccessLevelProp,
  DynamicIconKind,
  DynamicIconName,
  SpacingScale,
  TableHeadCellTooltipProp,
  VariantProp,
} from '@meterup/atto';
import type {
  Cell,
  ColumnDef,
  FilterFn,
  FilterFnOption,
  OnChangeFn,
  Row,
  RowData,
  RowSelectionState,
  Table as ReactTable,
  TableState as ReactTableState,
  VisibilityState,
} from '@tanstack/react-table';
import type { To } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BodySansSizes,
  Callout,
  CheckboxInput,
  colors,
  ControlGroup,
  darkThemeSelector,
  Divider,
  DynamicIcon,
  gutterCSS,
  HStack,
  selectors,
  shadows,
  SmallSansSizes,
  Stats,
  styled,
  UtilityControl,
} from '@meterup/atto';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  type ElementType,
  type ForwardedRef,
  type MouseEvent,
  type ReactNode,
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import type { AutoCollectionStateInterface, RowSelectionTracker } from './utils';
import { FLEXI_COLUMNS_ANIMATION_MS } from '../FlexiColumns';
import { useFlexiColumnsContext } from '../FlexiColumns/FlexiColumns';
import { EnhancedReactRouterLink } from '../ReactRouterLink';
import {
  createCSVExportHandler,
  createDefaultColumnVisibilityEffect,
  defaultColumnCanGlobalFilter,
  defaultGlobalFilterFn,
  filterFns,
  getPageRows,
  selectAllToggleHandler,
  sortingFns,
  useRowCheckboxHandlers,
  useSelectionTracker,
} from './utils';

const autoBlocksVars = {
  padding: {
    ends: 8,
    sides: 12,
  },
} satisfies {
  padding: {
    ends: SpacingScale;
    sides: SpacingScale;
  };
};

/** Same variant tokens as Button `callout`; use object form when a screen reader label is needed (e.g. unread). */
export type AutoBlocksRowCallout =
  | VariantProp
  | {
      variant: VariantProp;
      'aria-label'?: string | undefined;
    };

function resolveAutoBlocksCallout(
  spec: AutoBlocksRowCallout | null | undefined,
): { variant: VariantProp; ariaLabel?: string | undefined } | null {
  if (spec == null) return null;
  if (typeof spec === 'object' && spec !== null && 'variant' in spec) {
    return { variant: spec.variant, ariaLabel: spec['aria-label'] };
  }
  return { variant: spec };
}

export type AutoBlocksAlignmentProp = 'start' | 'center' | 'end';
export type AutoBlocksStuckProp = 'top' | 'right' | 'bottom' | 'left';
export type AutoBlocksOverflowProp = 'auto' | 'hidden' | 'scroll' | 'visible';

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-shadow
  interface ColumnMeta<TData extends RowData, TValue> {
    accessLevel?: AccessLevelProp | undefined | null;
    alignment?: AutoBlocksAlignmentProp;
    group?: string | null;
    hasField?: boolean;
    hideIfAllEqual?: boolean;
    hideIfAllNullish?: boolean;
    isLeading?: boolean;
    isSubline?: boolean;
    tooltip?: TableHeadCellTooltipProp;
    alwaysVisible?: boolean;
  }

  interface FilterFns {
    arrIncludesIfSet: FilterFn<any>;
    equalsIfSet: FilterFn<any>;
    prefixOverlapsIfSet: FilterFn<any>;
  }
}

function AutoBlocksItemSelector<D>({
  row,
  table,
  isRowSelectable = true,
  selectionTracker,
}: {
  row: Row<D>;
  table: ReactTable<D>;
  isRowSelectable?: boolean;
  selectionTracker: RowSelectionTracker;
}) {
  const { handleChange, handleClick } = useRowCheckboxHandlers(row, table, selectionTracker);

  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        // prevent click from propagating to row
        if (e.currentTarget.tagName === 'INPUT') return;
        e.stopPropagation();
      }}
    >
      <CheckboxInput
        aria-label={row.getIsSelected() ? 'Deselect row' : 'Select row'}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect() || !row.getCanMultiSelect() || !isRowSelectable}
        onChange={handleChange}
        onClick={handleClick}
      />
    </button>
  );
}

type GetLinkTo<D> = (row: D) => To | null | undefined;
type IsRowDisabled<D> = (row: D) => boolean;
type IsRowSelected<D> = (row: D) => boolean;
type IsRowDraggable<D> = (row: D) => boolean;
type OnRowDeselect<D> = (row: D) => void;
type OnRowClick<D> = (row: D) => void;
type OnRowReorder = (fromIndex: number, toIndex: number) => void;

const AutoBlocksPointContainer = styled('div', {
  ...SmallSansSizes,
  borderRadius: '$8',
  outline: 'none',

  variants: {
    isLeading: {
      true: {
        ...BodySansSizes,
        fontWeight: '$bold',
      },
      false: {},
    },
    isNavigable: {
      true: {},
      false: {},
    },
    isSelected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
    },
  },

  compoundVariants: [
    {
      isLeading: true,
      isNavigable: true,
      css: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },

        [selectors.hover]: {
          color: colors.headingBrandLight,

          [darkThemeSelector]: {
            color: colors.headingBrandDark,
          },
        },

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
    },
  ],
});
AutoBlocksPointContainer.displayName = 'AutoBlocksPointContainer';

function AutoBlocksPointImpl<D, E extends ElementType = 'div'>({
  as: asProp,
  applyLinkToAllCells,
  cell,
  hotkeyTos,
  isNavigable,
  isSelected,
  to,
  onClick,
}: {
  applyLinkToAllCells?: boolean;
  as?: E;
  cell: Cell<D, any>;
  hotkeyTos?: Record<string, To>;
  isMultiSelected?: boolean;
  isNavigable?: boolean;
  isSelected?: boolean;
  table: ReactTable<D>;
  to?: To;
  onClick?: React.MouseEventHandler<E | HTMLDivElement | typeof EnhancedReactRouterLink>;
}) {
  if (cell.column.columnDef.meta?.accessLevel === null) return null;
  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
  // const sizeProps = getSizeProps(table, cell.column);
  const meta = cell.column.columnDef.meta ?? {};
  const shouldUseLink = meta.isLeading || applyLinkToAllCells;
  const isNonLeadingWithLink = !meta.isLeading && applyLinkToAllCells;

  if (cell.column.columnDef.meta?.isLeading || cell.column.columnDef.meta?.isSubline) {
    const as = shouldUseLink ? asProp ?? EnhancedReactRouterLink : 'div';

    return (
      <AutoBlocksPointContainer
        key={cell.id}
        as={as}
        to={shouldUseLink ? to : undefined}
        tabIndex={isNonLeadingWithLink ? -1 : undefined}
        isLeading={meta.isLeading}
        isNavigable={isNavigable}
        isSelected={isSelected}
        onClick={onClick}
        {...(shouldUseLink ? { hotkeyTos } : {})}
        // {...sizeProps}
        // accessLevel={meta.accessLevel}
      >
        {content}
      </AutoBlocksPointContainer>
    );
  }

  return null;
}

const AutoBlocksPoint = memo(AutoBlocksPointImpl) as typeof AutoBlocksPointImpl;

function isCellEmpty<D>(cell: Cell<D, any>): boolean {
  if (cell.column.columnDef.meta?.accessLevel === null) return true;

  // Check the cell value first
  const value = cell.getValue();
  if (value == null || value === '') return true;
  if (typeof value === 'string' && value.trim() === '') return true;

  // Check if the rendered content would be empty
  try {
    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
    if (content == null || content === false) return true;
    if (typeof content === 'string' && content.trim() === '') return true;
    // If content is an array, check if all items are empty/null
    if (Array.isArray(content) && content.every((item) => item == null || item === '')) {
      return true;
    }
  } catch {
    // If rendering fails, consider it empty
    return true;
  }

  return false;
}

function LeadingOrSublineCells<D>({
  cells,
  kind,
  table,
  to,
  onRowLeadingClick,
  hotkeyTos,
  applyLinkToAllCells,
  isMultiSelected,
  isSelected,
  PointComponent,
  DividerComponent,
  isCellEmptyFn,
  LinkComponent,
}: {
  cells: Cell<D, unknown>[];
  kind: 'leading' | 'subline';
  table: ReactTable<D>;
  to: To | null | undefined;
  onRowLeadingClick?: OnRowClick<D>;
  hotkeyTos: Record<string, To>;
  applyLinkToAllCells?: boolean;
  isMultiSelected: boolean;
  isSelected: boolean;
  PointComponent: typeof AutoBlocksPointImpl;
  DividerComponent: React.ComponentType<{
    appearance?: 'thick';
    direction?: 'column';
    variant?: 'brand' | 'neutral';
  }>;
  isCellEmptyFn: (cell: Cell<D, unknown>) => boolean;
  LinkComponent: React.ComponentType<{ to: To }>;
}) {
  return (
    <>
      {cells.map((cell, index, allCells) => {
        const nextCell = allCells[index + 1];
        const isCurrentEmpty = isCellEmptyFn(cell);
        const isNextEmpty = nextCell ? isCellEmptyFn(nextCell) : true;
        const shouldShowDivider = index < allCells.length - 1 && !isCurrentEmpty && !isNextEmpty;

        return (
          <Fragment key={cell.id}>
            <PointComponent
              as={to ? LinkComponent : undefined}
              to={to ?? undefined}
              onClick={
                kind === 'leading' && onRowLeadingClick
                  ? () => onRowLeadingClick(cell.row.original)
                  : undefined
              }
              hotkeyTos={hotkeyTos}
              key={`${kind}-${cell.column.id}`}
              table={table}
              cell={cell}
              isNavigable={!!to}
              applyLinkToAllCells={applyLinkToAllCells}
              isMultiSelected={isMultiSelected}
              isSelected={isSelected}
            />
            {shouldShowDivider && (
              <DividerComponent
                appearance="thick"
                direction="column"
                variant={isSelected ? 'brand' : 'neutral'}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}

interface AutoBlocksItemProps<D> {
  callout?: (row: D) => AutoBlocksRowCallout | null | undefined;
  actions?: ReactNode | ((row: D) => ReactNode);
  applyLinkToAllCells?: boolean;
  'data-index'?: number;
  dragToReorder?: boolean;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  getLinkTo?: GetLinkTo<D>;
  icon?: {
    name: DynamicIconName;
    variant: VariantProp;
    priority?: DynamicIconKind[];
  };
  isDraggingRow?: boolean;
  isMultiSelected?: boolean;
  isRowDisabled?: IsRowDisabled<D>;
  isRowDraggable?: IsRowDraggable<D>;
  isRowSelected?: IsRowSelected<D>;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  onRowDeselect?: OnRowDeselect<D>;
  onRowReorder?: OnRowReorder;
  row: Row<D>;
  selectionTracker?: RowSelectionTracker;
  table: ReactTable<D>;
}

const AutoBlocksPointDragHandle = styled(UtilityControl, {
  cursor: 'grab',
  opacity: 0.5,

  [selectors.hover]: {
    cursor: 'grabbing',
    opacity: 1,
  },

  [selectors.focus]: {
    cursor: 'grabbing',
    opacity: 1,
  },
});
AutoBlocksPointDragHandle.displayName = 'AutoBlocksPointDragHandle';

const AutoBlocksItemHeaderEnd = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
AutoBlocksItemHeaderEnd.displayName = 'AutoBlocksItemHeaderEnd';

const AutoBlocksItemHeaderDivider = styled(Divider, {
  padding: '$2 0',
});
AutoBlocksItemHeaderDivider.displayName = 'AutoBlocksItemHeaderDivider';

const AutoBlocksItemHeaderContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
AutoBlocksItemHeaderContent.displayName = 'AutoBlocksItemHeaderContent';

const AutoBlocksItemHeader = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});
AutoBlocksItemHeader.displayName = 'AutoBlocksItemHeader';

const AutoBlocksItemDivider = styled(Divider);
AutoBlocksItemDivider.displayName = 'AutoBlocksItemDivider';

const AutoBlocksItemInfo = styled('div', {
  display: 'flex',
});
AutoBlocksItemInfo.displayName = 'AutoBlocksItemInfo';

const AutoBlocksItemWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  flexDirection: 'column',
});
AutoBlocksItemWrapper.displayName = 'AutoBlocksItemWrapper';

const AutoBlocksItemContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  heightAll: 'min-content',
  background: colors.transparent,
  strokeAll: colors.transparent,
  borderRadius: '$8',
  padding: `$${autoBlocksVars.padding.ends} $${autoBlocksVars.padding.sides}`,
  width: '100%',

  [darkThemeSelector]: {
    background: colors.transparent,
    strokeAll: colors.transparent,
  },

  '&:before, &:after': {
    content: '',
    position: 'absolute',
    right: `$${autoBlocksVars.padding.sides}`,
    left: `$${autoBlocksVars.padding.sides}`,
    display: 'flex',
    height: '$1',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  '&:before': {
    top: `-0.5px`,
  },

  '&:after': {
    bottom: `-0.5px`,
  },

  '&:first-child': {
    '&:before': {
      display: 'none',
    },
  },

  '&:last-child': {
    '&:after': {
      display: 'none',
    },
  },

  [selectors.hover]: {
    background: colors.bgNeutralLight,
    strokeAll: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.bgNeutralDark,
      strokeAll: colors.strokeNeutralDark,
    },
  },

  variants: {
    isDisabled: {
      true: {
        [selectors.all]: {
          opacity: 0.7,
          background: colors.bgNeutralLight,
          strokeAll: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.bgNeutralDark,
            strokeAll: colors.strokeNeutralDark,
          },
        },
      },
      false: {},
    },
    isDragging: {
      true: {
        opacity: 0.8,
      },
      false: {},
    },
    isSelected: {
      true: {
        [selectors.all]: {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
          },

          '&:before, &:after': {
            background: colors.transparent,

            [darkThemeSelector]: {
              background: colors.transparent,
            },
          },
        },
      },
      false: {},
    },
  },
});
AutoBlocksItemContainer.displayName = 'AutoBlocksItemContainer';

function AutoBlocksItemImpl<D>(
  {
    actions,
    applyLinkToAllCells,
    'data-index': dataIndex,
    dragToReorder,
    enableMultiRowSelection,
    getDrawerLinkTo,
    getEditLinkTo,
    getLinkTo,
    icon,
    isMultiSelected,
    isRowDisabled,
    isRowDraggable,
    onRowClick,
    onRowLeadingClick,
    isRowSelected,
    row,
    table,
    onRowReorder,
    onRowDeselect,
    callout,
    selectionTracker,
    ...remaining
  }: AutoBlocksItemProps<D>,
  ref?: ForwardedRef<HTMLDivElement>,
) {
  const to = getLinkTo?.(row.original);
  const editTo = getEditLinkTo?.(row.original);
  const drawerTo = getDrawerLinkTo?.(row.original);
  const hotkeyTos: Record<string, To> = {};
  if (editTo) {
    hotkeyTos.e = editTo;
  }
  if (drawerTo) {
    hotkeyTos.d = drawerTo;
  }
  const isDisabled = isRowDisabled?.(row.original) ?? false;
  const isSelected = isRowSelected?.(row.original) ?? false;
  const isDraggable = isRowDraggable?.(row.original) ?? true;

  // Determine if this specific row should be selectable for multi-row selection
  const isRowSelectable = useMemo(() => {
    if (typeof enableMultiRowSelection === 'function') {
      return enableMultiRowSelection(row);
    }
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection, row]);

  const handleClick = useCallback(() => {
    onRowClick?.(row.original);
  }, [onRowClick, row.original]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !dragToReorder || !isDraggable,
  });

  const onDrag = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleMouseDown = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  }, []);

  const AutoBlocksItemDragHandle = useCallback(() => {
    if (!dragToReorder) return null;

    return (
      <AutoBlocksPointDragHandle
        {...attributes}
        {...listeners}
        onMouseDown={handleMouseDown}
        active={isDragging}
        disabled={!isDraggable}
        icon="slide-vertical"
        label="Reorder"
        variant={isSelected || isMultiSelected ? 'brand' : 'neutral'}
      />
    );
  }, [
    attributes,
    dragToReorder,
    handleMouseDown,
    isDraggable,
    isDragging,
    listeners,
    isSelected,
    isMultiSelected,
  ]);

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      }
    },
    [setNodeRef, ref],
  );

  const stats = row
    .getVisibleCells()
    .filter((cell) => {
      const meta = cell.column.columnDef.meta ?? {};
      const isVisible = cell.column.getIsVisible();
      return !meta.isLeading && !meta.isSubline && isVisible;
    })
    .map((cell) => ({
      label: String(cell.column.columnDef.header || ''),
      value:
        typeof cell.column.columnDef.cell === 'function'
          ? cell.column.columnDef.cell(cell.getContext())
          : String(cell.getValue() || ''),
      variant: (isSelected || isMultiSelected ? 'brand' : 'neutral') as 'brand' | 'neutral',
    }));

  const isAlignmentEnd = (meta: { alignment?: string } | undefined) => meta?.alignment === 'end';
  const leadingCells = row
    .getVisibleCells()
    .filter((cell) => cell.column.columnDef.meta?.isLeading);
  const sublineCells = row
    .getVisibleCells()
    .filter((cell) => cell.column.columnDef.meta?.isSubline);
  const startLeading = leadingCells.filter((cell) => !isAlignmentEnd(cell.column.columnDef.meta));
  const startSubline = sublineCells.filter((cell) => !isAlignmentEnd(cell.column.columnDef.meta));
  const endLeading = leadingCells.filter((cell) => isAlignmentEnd(cell.column.columnDef.meta));
  const endSubline = sublineCells.filter((cell) => isAlignmentEnd(cell.column.columnDef.meta));
  const hasActions = actions || dragToReorder || enableMultiRowSelection;
  const hasSublineContent = startSubline.length > 0 || endSubline.length > 0;

  const leadingSublineProps = {
    table,
    to,
    onRowLeadingClick,
    hotkeyTos,
    applyLinkToAllCells,
    isMultiSelected: isMultiSelected ?? false,
    isSelected: (isSelected || isMultiSelected) ?? false,
    PointComponent: AutoBlocksPoint,
    DividerComponent: AutoBlocksItemHeaderDivider,
    isCellEmptyFn: isCellEmpty,
    LinkComponent: EnhancedReactRouterLink,
  };

  const calloutResolved = callout ? resolveAutoBlocksCallout(callout(row.original)) : null;

  return (
    <AutoBlocksItemContainer
      ref={combinedRef}
      key={row.id}
      isDisabled={isDisabled}
      isDragging={isDragging}
      isSelected={isSelected || isMultiSelected}
      onClick={handleClick}
      data-index={dataIndex}
      data-item-id={row.id}
      style={onDrag}
      {...remaining}
    >
      <AutoBlocksItemWrapper>
        <AutoBlocksItemHeader>
          {calloutResolved && (
            <Callout
              position={{ top: 4, right: 4 }}
              variant={calloutResolved.variant}
              {...(calloutResolved.ariaLabel != null
                ? { 'aria-label': calloutResolved.ariaLabel }
                : {})}
            />
          )}
          {icon && (
            <DynamicIcon
              icon={icon.name}
              priority={icon.priority}
              size={32}
              variant={icon.variant}
            />
          )}
          <AutoBlocksItemHeaderContent>
            {(startLeading.length > 0 || endLeading.length > 0) && (
              <HStack align="center" justify="between" spacing={6} width="full">
                <HStack align="center" spacing={6}>
                  <LeadingOrSublineCells
                    cells={startLeading}
                    kind="leading"
                    {...leadingSublineProps}
                  />
                </HStack>
                <HStack align="center" spacing={6}>
                  <LeadingOrSublineCells
                    cells={endLeading}
                    kind="leading"
                    {...leadingSublineProps}
                  />
                  {hasActions && !hasSublineContent && (
                    <AutoBlocksItemHeaderEnd>
                      {actions && (
                        <ControlGroup>
                          {typeof actions === 'function' && actions(row.original)}
                        </ControlGroup>
                      )}
                      {enableMultiRowSelection && selectionTracker && (
                        <AutoBlocksItemSelector
                          row={row}
                          table={table}
                          isRowSelectable={isRowSelectable}
                          selectionTracker={selectionTracker}
                        />
                      )}
                      {dragToReorder && <AutoBlocksItemDragHandle />}
                    </AutoBlocksItemHeaderEnd>
                  )}
                </HStack>
              </HStack>
            )}
            {hasSublineContent && (
              <HStack align="center" justify="between" spacing={6} width="full">
                <HStack align="center" spacing={6}>
                  <LeadingOrSublineCells
                    cells={startSubline}
                    kind="subline"
                    {...leadingSublineProps}
                  />
                </HStack>
                <HStack align="center" spacing={6}>
                  <LeadingOrSublineCells
                    cells={endSubline}
                    kind="subline"
                    {...leadingSublineProps}
                  />
                  {hasActions && (
                    <AutoBlocksItemHeaderEnd>
                      {actions && (
                        <ControlGroup>
                          {typeof actions === 'function' && actions(row.original)}
                        </ControlGroup>
                      )}
                      {enableMultiRowSelection && selectionTracker && (
                        <AutoBlocksItemSelector
                          row={row}
                          table={table}
                          isRowSelectable={isRowSelectable}
                          selectionTracker={selectionTracker}
                        />
                      )}
                      {dragToReorder && <AutoBlocksItemDragHandle />}
                    </AutoBlocksItemHeaderEnd>
                  )}
                </HStack>
              </HStack>
            )}
          </AutoBlocksItemHeaderContent>
        </AutoBlocksItemHeader>
        {stats.length > 0 ? (
          <AutoBlocksItemInfo>
            <Stats
              appearance="dividers"
              gutter={{ edge: 'none' }}
              gap={12}
              proportions="auto"
              size="small"
              stats={stats}
            />
          </AutoBlocksItemInfo>
        ) : null}
      </AutoBlocksItemWrapper>
    </AutoBlocksItemContainer>
  );
}

export const AutoBlocksItem = forwardRef(AutoBlocksItemImpl) as <D>(
  props: AutoBlocksItemProps<D> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof AutoBlocksItemImpl<D>>;

const AutoBlocksListContainerWrapper = styled('div', {
  display: 'grid',
  widthAll: '100%',
});
AutoBlocksListContainerWrapper.displayName = 'AutoBlocksListContainerWrapper';

function AutoBlocksListContainer({ children }: { children: ReactNode }) {
  const css = gutterCSS({
    context: 'application',
    edge: 'all',
    adjustment: { ends: autoBlocksVars.padding.ends, sides: autoBlocksVars.padding.sides },
  });

  return <AutoBlocksListContainerWrapper css={css}>{children}</AutoBlocksListContainerWrapper>;
}

interface AutoBlocksBodyProps<D> {
  applyLinkToAllCells?: boolean;
  dragToReorder?: boolean;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  getLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowDraggable?: IsRowDraggable<D>;
  isRowSelected?: IsRowSelected<D>;
  multiSelectedRows?: RowSelectionState;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  onRowDeselect?: OnRowDeselect<D>;
  onRowReorder?: OnRowReorder;
  blockActions?: (row: D) => ReactNode;
  blockIcon?: (row: D) => {
    name: DynamicIconName;
    variant: VariantProp;
    priority?: DynamicIconKind[];
  };
  blockCallout?: (row: D) => AutoBlocksRowCallout | null | undefined;
  contentAfterRowId?: string;
  contentAfterRow?: ReactNode;
  rows: Row<D>[];
  selectionTracker?: RowSelectionTracker;
  table: ReactTable<D>;
}

function AutoBlocksList<D>({
  applyLinkToAllCells,
  dragToReorder,
  enableMultiRowSelection,
  getDrawerLinkTo,
  getEditLinkTo,
  getLinkTo,
  isRowDisabled,
  isRowDraggable,
  isRowSelected,
  multiSelectedRows,
  onRowClick,
  onRowLeadingClick,
  onRowDeselect,
  onRowReorder,
  blockActions,
  blockCallout,
  blockIcon,
  contentAfterRowId,
  contentAfterRow,
  rows,
  selectionTracker,
  table,
}: AutoBlocksBodyProps<D>) {
  return (
    <AutoBlocksListContainer>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <AutoBlocksItem
            row={row}
            table={table}
            icon={blockIcon ? blockIcon(row.original) : undefined}
            callout={blockCallout}
            isRowDisabled={isRowDisabled}
            isRowSelected={isRowSelected}
            getLinkTo={getLinkTo}
            getDrawerLinkTo={getDrawerLinkTo}
            getEditLinkTo={getEditLinkTo}
            onRowClick={onRowClick}
            onRowLeadingClick={onRowLeadingClick}
            onRowDeselect={onRowDeselect}
            actions={blockActions ? () => blockActions(row.original) : undefined}
            isMultiSelected={multiSelectedRows?.[row.id]}
            applyLinkToAllCells={applyLinkToAllCells}
            dragToReorder={dragToReorder}
            isRowDraggable={isRowDraggable}
            onRowReorder={onRowReorder}
            enableMultiRowSelection={enableMultiRowSelection}
            selectionTracker={selectionTracker}
          />
          {row.id === contentAfterRowId && contentAfterRow}
        </Fragment>
      ))}
    </AutoBlocksListContainer>
  );
}

// react-table throws errors when getting row model if the sort/filter
// state is invalid. This is probably just because of the global URL-based
// search state becoming outdated, so we try resetting the filter and sort
// state and getting the rows again once.
function useRows<D>(table: ReactTable<D>) {
  try {
    return table.getRowModel()?.rows ?? [];
  } catch (err) {
    table.resetSorting();
    table.resetGlobalFilter();
    table.resetColumnFilters();
    return table.getRowModel()?.rows ?? [];
  }
}

function useCoreRows<D>(table: ReactTable<D>) {
  try {
    return table.getCoreRowModel()?.rows ?? [];
  } catch (err) {
    table.resetSorting();
    table.resetGlobalFilter();
    table.resetColumnFilters();
    return table.getCoreRowModel()?.rows ?? [];
  }
}

export interface AutoBlocksHandle<D = any> {
  exportToCSV: () => void;
  filteredRows: Row<D>[];
}

export function AutoBlocks<D, C extends ColumnDef<D, any>>({
  applyLinkToAllCells,
  columns,
  data,
  dragToReorder = false,
  emptyState,
  enableMultiRowSelection = false,
  enableSorting = true,
  exportToCSVFilename,
  getDrawerLinkTo,
  getEditLinkTo,
  getLinkTo,
  getRowId,
  globalFilterFn = defaultGlobalFilterFn,
  isRowDisabled,
  isRowDraggable,
  isRowSelected,
  multiSelectedRows,
  onRowClick,
  onRowLeadingClick,
  onRowDeselect,
  onRowMultiSelectionChange,
  onRowReorder,
  onRowsChange,
  blockActions,
  blockCallout,
  blockIcon,
  contentAfterRowId,
  contentAfterRow,
  tableState,
}: {
  applyLinkToAllCells?: boolean;
  columns: C[];
  data: D[];
  dragToReorder?: boolean;
  emptyState?: ReactNode;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  enableSorting?: boolean;
  exportToCSVFilename?: string;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  getLinkTo?: GetLinkTo<D>;
  getRowId?: (originalRow: D, index: number, parent?: Row<D>) => string;
  globalFilterFn?: FilterFnOption<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowDraggable?: IsRowDraggable<D>;
  isRowSelected?: IsRowSelected<D>;
  multiSelectedRows?: RowSelectionState;
  onRowClick?: OnRowClick<D>;
  onRowLeadingClick?: OnRowClick<D>;
  onRowDeselect?: OnRowDeselect<D>;
  onRowMultiSelectionChange?: OnChangeFn<RowSelectionState>;
  onRowReorder?: OnRowReorder;
  onRowsChange?: (rows: Row<D>[]) => void;
  blockActions?: (row: D) => ReactNode;
  blockIcon?: (row: D) => {
    name: DynamicIconName;
    variant: VariantProp;
    priority?: DynamicIconKind[];
  };
  blockCallout?: (row: D) => AutoBlocksRowCallout | null | undefined;
  contentAfterRowId?: string;
  contentAfterRow?: ReactNode;
  tableState: AutoCollectionStateInterface<D, NonNullable<C['id']>>;
}) {
  const flexiContext = useFlexiColumnsContext();

  const effectiveOnRowLeadingClick =
    onRowLeadingClick ?? (flexiContext ? () => flexiContext.setActiveColumn('end') : undefined);
  const effectiveOnRowDeselect =
    onRowDeselect ??
    (flexiContext
      ? () => {
          flexiContext.setActiveColumn('start');
          if (flexiContext.onDeselect) {
            setTimeout(flexiContext.onDeselect, FLEXI_COLUMNS_ANIMATION_MS);
          }
        }
      : undefined);
  const effectiveIsRowSelected =
    isRowSelected ??
    (flexiContext && getRowId
      ? (row: D) => getRowId(row, 0) === (flexiContext.selectedId ?? null)
      : undefined);

  const isMultiRowSelectionEnabled = useMemo(() => {
    if (typeof enableMultiRowSelection === 'boolean') {
      return enableMultiRowSelection;
    }
    if (typeof enableMultiRowSelection === 'function') {
      return true; // Function-based selection is enabled
    }
    // For AccessLevelProp, we'll need to check permissions
    // For now, we'll assume it's enabled if it's an access level and not null
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection]);

  const state = useMemo(() => {
    const s: Partial<ReactTableState> = {
      globalFilter: tableState.globalFilter,
    };

    if (multiSelectedRows) {
      s.rowSelection = multiSelectedRows;
    }

    if (tableState.sortingState) {
      s.sorting = tableState.sortingState;
    }

    if (tableState.columnVisibility) {
      s.columnVisibility = tableState.columnVisibility as VisibilityState;
    }

    if (tableState.columnFilters) {
      s.columnFilters = tableState.columnFilters;
    }

    if (tableState.columnOrder) {
      s.columnOrder = tableState.columnOrder;
    }

    return s;
  }, [tableState, multiSelectedRows]);

  const table = useReactTable({
    data,
    columns,
    filterFns,
    sortingFns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    enableGlobalFilter: true,
    getColumnCanGlobalFilter: defaultColumnCanGlobalFilter,
    globalFilterFn,
    onSortingChange: (value) => {
      if (typeof value === 'function') {
        tableState.setSortingState(value(tableState.sortingState));
      } else {
        tableState.setSortingState(value);
      }
    },
    enableMultiRowSelection: isMultiRowSelectionEnabled,
    onRowSelectionChange: onRowMultiSelectionChange,
    getRowId,
    state,
    onColumnFiltersChange: (value) => {
      if (typeof value === 'function') {
        tableState.setColumnFilters(value(tableState.columnFilters));
      } else {
        tableState.setColumnFilters(value);
      }
    },
    onColumnOrderChange: (value) => {
      if (typeof value === 'function') {
        tableState.setColumnOrder(value(tableState.columnOrder));
      } else {
        tableState.setColumnOrder(value);
      }
    },
  });

  // Use shared CSV export handler
  const exportToCSV = useMemo(
    () => createCSVExportHandler(table, exportToCSVFilename),
    [table, exportToCSVFilename],
  );

  const filteredRows = useRows(table);
  const coreRows = useCoreRows(table);

  const pageRows = useMemo(
    () => getPageRows(filteredRows, tableState.offset, tableState.limit),
    [filteredRows, tableState.offset, tableState.limit],
  );

  const handleSelectAllToggle = useCallback(
    () => selectAllToggleHandler(table, enableMultiRowSelection, multiSelectedRows, pageRows),
    [table, enableMultiRowSelection, multiSelectedRows, pageRows],
  );

  // Add hotkey handler for select all when multi-row selection is enabled
  useHotkeys(
    'alt+a',
    (e) => {
      if (!isMultiRowSelectionEnabled) return;
      e.preventDefault();

      // Trigger select all functionality
      handleSelectAllToggle();
    },
    {
      enableOnFormTags: true,
    },
    [isMultiRowSelectionEnabled, handleSelectAllToggle],
  );

  const selectionTracker = useSelectionTracker();

  const { setColumnVisibility } = table;

  // Use shared column visibility effect
  const columnVisibilityEffect = useCallback(
    () =>
      createDefaultColumnVisibilityEffect(
        columns,
        coreRows,
        setColumnVisibility,
        tableState.columnVisibility as VisibilityState,
      ),
    [columns, coreRows, setColumnVisibility, tableState.columnVisibility],
  );

  useEffect(() => {
    columnVisibilityEffect();
  }, [columnVisibilityEffect]);

  useImperativeHandle(
    tableState.tableRef,
    () => ({
      exportToCSV,
      filteredRows,
      data,
    }),
    [exportToCSV, filteredRows, data],
  );

  useEffect(() => {
    onRowsChange?.(filteredRows);
  }, [filteredRows, onRowsChange]);

  const rows = pageRows;

  // DnD sensors - hooks must be called directly, not in useMemo
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!onRowReorder || !over || active.id === over.id) {
        return;
      }

      const activeIndex = rows.findIndex((row) => row.id === active.id);
      const overIndex = rows.findIndex((row) => row.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        onRowReorder(activeIndex, overIndex);
      }
    },
    [onRowReorder, rows],
  );

  const [isDraggingRow, setIsDraggingRow] = useState(false);
  const [, setActiveId] = useState<string | null>(null);
  const [, setMousePosition] = useState({ x: 0, y: 0 });
  const mousePositionRef = useRef({ x: 0, y: 0 });

  // Global mouse move listener for tracking mouse position during drag
  useEffect(() => {
    if (isDraggingRow) {
      let rafId: number;
      const handleGlobalMouseMove = (e: Event) => {
        const mouseEvent = e as unknown as { clientX: number; clientY: number };
        // Update ref immediately for smooth tracking
        mousePositionRef.current = { x: mouseEvent.clientX, y: mouseEvent.clientY };
        // Throttle state updates to reduce re-renders
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          setMousePosition({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        });
      };

      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
    return undefined;
  }, [isDraggingRow]);

  if (emptyState && rows.length === 0) {
    return emptyState;
  }

  const tableContent = (
    <AutoBlocksList
      applyLinkToAllCells={applyLinkToAllCells}
      dragToReorder={dragToReorder}
      enableMultiRowSelection={enableMultiRowSelection}
      getDrawerLinkTo={getDrawerLinkTo}
      getEditLinkTo={getEditLinkTo}
      getLinkTo={getLinkTo}
      isRowDisabled={isRowDisabled}
      isRowDraggable={isRowDraggable}
      isRowSelected={effectiveIsRowSelected}
      multiSelectedRows={multiSelectedRows}
      onRowClick={onRowClick}
      onRowLeadingClick={effectiveOnRowLeadingClick}
      onRowDeselect={effectiveOnRowDeselect}
      onRowReorder={onRowReorder}
      blockCallout={blockCallout}
      blockActions={blockActions}
      blockIcon={blockIcon}
      contentAfterRowId={contentAfterRowId}
      contentAfterRow={contentAfterRow}
      rows={rows}
      selectionTracker={selectionTracker}
      table={table}
    />
  );

  if (dragToReorder) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => {
          const draggedId = event.active.id as string;
          setIsDraggingRow(true);
          setActiveId(draggedId);
        }}
        onDragEnd={(event) => {
          setIsDraggingRow(false);
          handleDragEnd(event);
        }}
      >
        <SortableContext
          items={(() => {
            if (isDraggingRow) return rowIds;
            return dragToReorder ? rowIds : [];
          })()}
          strategy={verticalListSortingStrategy}
        >
          {tableContent}
        </SortableContext>
      </DndContext>
    );
  }

  return tableContent;
}
